import { z } from 'zod';
import { CURRENCY_CODES, mulDiv, type CurrencyCode } from './money.js';
import { PAYMENT_METHODS, type PaymentMethod } from './catalog.js';

/**
 * Phase 5: exchange rates and payment set-up.
 *
 * A rate says how many units of a foreign currency equal one unit of the base currency
 * (e.g. base USD, ZWG rate 26.85 means US$1 = ZiG 26.85). Rates are kept as integer
 * millionths ("micros") so conversions are exact integer arithmetic.
 */

export const RATE_SCALE = 1_000_000;

/** "26.85" → 26_850_000. Up to six decimals; null if not a positive number. */
export function parseRate(text: string): number | null {
  const m = /^\s*(\d{1,7})(?:\.(\d{1,6}))?\s*$/.exec(text);
  if (!m) return null;
  const micros = Number(m[1]) * RATE_SCALE + Number((m[2] ?? '').padEnd(6, '0'));
  return micros > 0 ? micros : null;
}

/** 26_850_000 → "26.85" (at least two decimals, trailing zeros trimmed beyond that). */
export function formatRate(micros: number): string {
  const whole = Math.floor(micros / RATE_SCALE);
  const frac = String(micros % RATE_SCALE).padStart(6, '0').replace(/0+$/, '').padEnd(2, '0');
  return `${whole}.${frac}`;
}

/** Foreign cents → base cents at the rate (rounded half away from zero). */
export function toBaseCents(foreignCents: number, rateMicros: number): number {
  return mulDiv(foreignCents, RATE_SCALE, rateMicros);
}

/** Base cents → foreign cents at the rate (rounded half away from zero). */
export function fromBaseCents(baseCents: number, rateMicros: number): number {
  return mulDiv(baseCents, rateMicros, RATE_SCALE);
}

/**
 * What to ask for in a foreign currency so that, converted back, it covers the base amount.
 * Rounded up, so paying exactly this is never "short by a cent".
 */
export function dueInCurrency(baseCents: number, rateMicros: number): number {
  if (baseCents <= 0) return 0;
  const n = BigInt(baseCents) * BigInt(rateMicros);
  const d = BigInt(RATE_SCALE);
  return Number((n + d - 1n) / d);
}

export const exchangeRateSchema = z.object({
  currency: z.enum(CURRENCY_CODES as [CurrencyCode, ...CurrencyCode[]]),
  rate: z
    .string()
    .trim()
    .refine((v) => parseRate(v) != null, 'Enter the rate as a number, e.g. 26.85'),
  source: z
    .string()
    .trim()
    .max(60)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type ExchangeRateInput = z.infer<typeof exchangeRateSchema>;

/** A big move is usually a typing mistake (26.85 entered as 268.5); the UI asks to confirm. */
export function rateChangeIsLarge(oldMicros: number, newMicros: number): boolean {
  return newMicros > oldMicros * 1.25 || newMicros < oldMicros * 0.8;
}

/* ── Payment methods ────────────────────────────────────────────────── */

/** Methods a business can switch off. Account and deposit follow their own modules. */
export const CONFIGURABLE_METHODS = ['cash', 'card', 'ecocash', 'onemoney', 'innbucks', 'omari', 'bank_transfer'] as const satisfies readonly PaymentMethod[];
export type ConfigurableMethod = (typeof CONFIGURABLE_METHODS)[number];

export interface PaymentMethodSetting {
  enabled: boolean;
  /** Merchant code, till number or bank account customers pay into (shown at the till). */
  account: string | null;
}
export type PaymentSettings = Partial<Record<ConfigurableMethod, PaymentMethodSetting>>;

export const paymentSettingsSchema = z
  .partialRecord(
    z.enum(CONFIGURABLE_METHODS),
    z.object({
      enabled: z.boolean(),
      account: z
        .string()
        .trim()
        .max(60)
        .transform((v) => (v === '' ? null : v))
        .nullable()
        .default(null),
    }),
  )
  .refine((v) => (v.cash?.enabled ?? true) || Object.values(v).some((m) => m?.enabled), { message: 'Keep at least one payment method switched on' });

/** Defaults: every method on. Missing entries count as on. */
export function methodEnabled(settings: PaymentSettings | null | undefined, method: PaymentMethod): boolean {
  if (!(CONFIGURABLE_METHODS as readonly string[]).includes(method)) return true;
  return settings?.[method as ConfigurableMethod]?.enabled ?? true;
}

export function enabledMethods(settings: PaymentSettings | null | undefined): ConfigurableMethod[] {
  return CONFIGURABLE_METHODS.filter((m) => methodEnabled(settings, m));
}

/** Mobile money and bank references must be unique: an old SMS cannot pay for a second sale. */
export function referenceMustBeUnique(method: PaymentMethod): boolean {
  return PAYMENT_METHODS[method].needsReference;
}
